import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { PasswordInput } from "@/components/PasswordInput";
import { ApiPaths, api } from "@/lib/api";
import { useLogin, useWebAuthnLogin } from "@/lib/auth";
import { safeNextPath } from "@/lib/safeNext";
import { browserSupportsWebAuthn } from "@/lib/webauthn";
import { OrgPicker } from "./OrgPicker";

type FormValues = { email: string; password: string };

export function LoginForm() {
  const login = useLogin();
  const passkeyLogin = useWebAuthnLogin();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  // Where to land after signing in. `?next=` is how an invitation link
  // keeps its place across the sign-in detour; without honouring it the
  // invite was simply forgotten and the user arrived on the dashboard
  // wondering where it went. Validated, because a redirect target that
  // arrives in the URL is attacker-controlled.
  const after = safeNextPath(params.get("next")) ?? "/dashboard";
  const { register, handleSubmit, formState, getValues } = useForm<FormValues>({
    defaultValues: { email: "", password: "" },
  });
  const [serverError, setServerError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await login.mutateAsync(values);
      // After login, /auth/me will tell us the membership count. If multi-
      // org, OrgPicker re-prompts; if single, we redirect to dashboard.
      setPickerOpen(true);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setServerError(detail ?? t("auth.loginFailed"));
    }
  });

  /** Phase 3-sso — check whether the entered email's domain has an
   * OrgIdpConfig. On 200 we navigate to the IdP; on 404 we surface a
   * "no SSO for this domain" note; on other errors we surface a
   * generic message. The password fields stay editable so users with
   * SSO opt-out can still use a local password. */
  const signInWithPasskey = async () => {
    setServerError(null);
    try {
      await passkeyLogin.mutateAsync();
      setPickerOpen(true);
    } catch (e) {
      // User cancelling the OS prompt throws NotAllowedError — stay quiet.
      if ((e as Error)?.name === "NotAllowedError") return;
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setServerError(detail ?? t("auth.passkeyFailed", "Passkey sign-in failed"));
    }
  };

  const continueWithSso = async () => {
    setServerError(null);
    const email = (getValues("email") || "").trim();
    if (!email) {
      setServerError(t("auth.emailRequired"));
      return;
    }
    setSsoChecking(true);
    try {
      const res = await api.post<{ auth_url: string }>(
        ApiPaths.authSsoInitiate,
        { email },
      );
      window.location.href = res.data.auth_url;
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setServerError(t("auth.loginFailed"));
      } else {
        setServerError(t("auth.loginFailed"));
      }
    } finally {
      setSsoChecking(false);
    }
  };

  if (pickerOpen) {
    return (
      <OrgPicker
        onPicked={() => navigate(after, { replace: true })}
        onSingleMembership={() => navigate(after, { replace: true })}
      />
    );
  }

  return (
    <form onSubmit={onSubmit} className="card mx-auto mt-12 max-w-md space-y-5" noValidate>
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-nb-text">{t("auth.signIn")}</h1>
        <LocaleSwitcher />
      </div>

      <div>
        <label className="label" htmlFor="email">{t("auth.email")}</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          className="input"
          aria-invalid={formState.errors.email ? true : undefined}
          {...register("email", { required: true })}
        />
        {formState.errors.email && (
          <p className="mt-1 text-sm text-cvp-danger">{t("auth.emailRequired")}</p>
        )}
      </div>

      <div>
        <div className="mb-1 flex items-baseline justify-between">
          <label className="label mb-0" htmlFor="password">{t("auth.password")}</label>
          <a className="text-xs text-nb-mint hover:underline" href="/forgot-password">
            {t("auth.forgotPassword")}
          </a>
        </div>
        <PasswordInput
          id="password"
          autoComplete="current-password"
          aria-invalid={formState.errors.password ? true : undefined}
          {...register("password", { required: true, minLength: 12 })}
        />
        <p
          className={`mt-1 text-xs ${
            formState.errors.password ? "text-cvp-danger" : "text-nb-text-2"
          }`}
        >
          {t("auth.passwordMinLengthHint")}
        </p>
      </div>

      {serverError && (
        <p role="alert" className="text-sm text-cvp-danger">{serverError}</p>
      )}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={formState.isSubmitting || login.isPending}
      >
        {login.isPending ? t("auth.signingIn") : t("auth.signIn")}
      </button>

      <button
        type="button"
        className="btn-ghost w-full"
        onClick={continueWithSso}
        disabled={ssoChecking}
      >
        {ssoChecking ? t("common.loading") : t("auth.ssoSignIn")}
      </button>

      {browserSupportsWebAuthn() && (
        <button
          type="button"
          className="btn-ghost w-full"
          onClick={signInWithPasskey}
          disabled={passkeyLogin.isPending}
        >
          {passkeyLogin.isPending
            ? t("auth.signingIn")
            : t("auth.passkeySignIn", "Sign in with a passkey")}
        </button>
      )}

      <p className="text-center text-xs text-nb-text-2">
        {t("auth.noAccount")}{" "}
        <a className="text-nb-mint hover:underline" href="/signup">
          {t("auth.signUp")}
        </a>
      </p>
    </form>
  );
}